import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  ChefHat, 
  Clock, 
  DollarSign, 
  Users, 
  BarChart3,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  Workflow
} from "lucide-react";

// Sample data for kitchen efficiency
const recipeComplexityData = [
  { name: 'Ropa Vieja Canaria', complexity: 85, prepTime: 45, laborCost: 18.50 },
  { name: 'Ensalada de Palmitos', complexity: 30, prepTime: 12, laborCost: 4.80 },
  { name: 'Risotto de Gofio', complexity: 65, prepTime: 28, laborCost: 11.20 },
  { name: 'Cherne Frito con Papas', complexity: 55, prepTime: 25, laborCost: 10.00 },
  { name: 'Bienmesabe', complexity: 80, prepTime: 35, laborCost: 14.00 },
  { name: 'Hamburguesa con Papas Arrugadas', complexity: 50, prepTime: 20, laborCost: 8.00 },
  { name: 'Fideos con Mojo', complexity: 45, prepTime: 18, laborCost: 7.20 },
];

const bottleneckData = [
  { 
    station: 'Estación de Parrilla', 
    utilization: 85, 
    avgWaitTime: 12, 
    bottleneckReason: 'Alto volumen de pedidos de carne durante horas pico',
    recommendation: 'Añadir parrilla secundaria para horas pico'
  },
  { 
    station: 'Preparación Fría', 
    utilization: 65, 
    avgWaitTime: 5, 
    bottleneckReason: 'Espacio de trabajo limitado para varios cocineros',
    recommendation: 'Reorganizar las mesas de preparación para un flujo más eficiente'
  },
  { 
    station: 'Estación de Frituras', 
    utilization: 90, 
    avgWaitTime: 15, 
    bottleneckReason: 'Una sola freidora manejando múltiples platos',
    recommendation: 'Añadir freidora dedicada para entrantes'
  },
  { 
    station: 'Área de Emplatado', 
    utilization: 75, 
    avgWaitTime: 8, 
    bottleneckReason: 'Congestión durante períodos de alto volumen',
    recommendation: 'Crear áreas de emplatado separadas para entrantes y platos principales'
  },
  { 
    station: 'Zona de Lavado', 
    utilization: 95, 
    avgWaitTime: 20, 
    bottleneckReason: 'Capacidad insuficiente de lavado durante horas pico',
    recommendation: 'Añadir segundo lavaplatos durante el servicio de cena'
  },
];

const laborCostData = [
  { 
    item: 'Ropa Vieja Canaria', 
    prepTime: 45,
    cookTime: 35, 
    platingTime: 3,
    totalTime: 83, 
    laborCost: 18.50,
    percentOfPrice: 24
  },
  { 
    item: 'Ensalada de Palmitos', 
    prepTime: 12,
    cookTime: 0, 
    platingTime: 2,
    totalTime: 14, 
    laborCost: 4.80,
    percentOfPrice: 32
  },
  { 
    item: 'Risotto de Gofio', 
    prepTime: 15,
    cookTime: 25, 
    platingTime: 2,
    totalTime: 42, 
    laborCost: 11.20,
    percentOfPrice: 28
  },
  { 
    item: 'Cherne Frito con Papas', 
    prepTime: 15,
    cookTime: 12, 
    platingTime: 1,
    totalTime: 28, 
    laborCost: 10.00,
    percentOfPrice: 20
  },
  { 
    item: 'Bienmesabe', 
    prepTime: 20,
    cookTime: 18, 
    platingTime: 3,
    totalTime: 41, 
    laborCost: 14.00,
    percentOfPrice: 35
  },
];

const stationData = [
  { 
    station: 'Estación de Parrilla', 
    ingredientCount: 12, 
    menuItems: 8, 
    utilizationRate: 85,
    recommendation: 'Combinar con la estación de gratinado para reducir movimiento'
  },
  { 
    station: 'Estación de Frituras', 
    ingredientCount: 8, 
    menuItems: 10, 
    utilizationRate: 90,
    recommendation: 'Añadir freidora dedicada para entrantes'
  },
  { 
    station: 'Preparación Fría', 
    ingredientCount: 25, 
    menuItems: 15, 
    utilizationRate: 65,
    recommendation: 'Reorganizar en dos áreas de preparación separadas'
  },
  { 
    station: 'Línea Caliente', 
    ingredientCount: 20, 
    menuItems: 12, 
    utilizationRate: 80,
    recommendation: 'Reposicionar para reducir pasos a refrigeración'
  },
  { 
    station: 'Estación de Postres', 
    ingredientCount: 15, 
    menuItems: 6, 
    utilizationRate: 50,
    recommendation: 'Combinar con preparación fría durante períodos lentos'
  },
];

const benchmarkingData = [
  {
    month: 'Jan',
    yourRestaurant: 68,
    industryAverage: 65,
    topPerformers: 78,
  },
  {
    month: 'Feb',
    yourRestaurant: 70,
    industryAverage: 66,
    topPerformers: 79,
  },
  {
    month: 'Mar',
    yourRestaurant: 72,
    industryAverage: 67,
    topPerformers: 80,
  },
  {
    month: 'Apr',
    yourRestaurant: 69,
    industryAverage: 66,
    topPerformers: 81,
  },
  {
    month: 'May',
    yourRestaurant: 74,
    industryAverage: 68,
    topPerformers: 82,
  },
  {
    month: 'Jun',
    yourRestaurant: 78,
    industryAverage: 70,
    topPerformers: 83,
  },
];

// Color constants
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#9c27b0', '#e91e63'];

const OperationalEfficiencyAnalyzer = () => {
  const [selectedMetric, setSelectedMetric] = useState('laborEfficiency');

  // Function to safely format numbers
  const formatNumber = (value: number | string): string => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    return String(value);
  };
  
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-3xl font-semibold tracking-tight">Operational Efficiency Analyzer</h1>
        
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Export Report
          </Button>
          
          <Button className="flex items-center gap-2">
            <Workflow className="h-4 w-4" />
            Optimize Workflow
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Average Prep Time</p>
              <h3 className="text-2xl font-bold mt-1">24.5 min</h3>
              <p className="text-xs flex items-center mt-1 text-red-600">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +2.3% vs last month
              </p>
            </div>
            <Clock className="h-8 w-8 text-muted-foreground" />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Avg Labor Cost/Item</p>
              <h3 className="text-2xl font-bold mt-1">$9.70</h3>
              <p className="text-xs flex items-center mt-1 text-green-600">
                <ArrowDownRight className="h-3 w-3 mr-1" />
                -1.8% vs last month
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-muted-foreground" />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Kitchen Efficiency</p>
              <h3 className="text-2xl font-bold mt-1">72%</h3>
              <p className="text-xs flex items-center mt-1 text-green-600">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +4.5% vs last month
              </p>
            </div>
            <ChefHat className="h-8 w-8 text-muted-foreground" />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Staff Productivity</p>
              <h3 className="text-2xl font-bold mt-1">85%</h3>
              <p className="text-xs flex items-center mt-1 text-green-600">
                <ArrowUpRight className="h-3 w-3 mr-1" />
                +2.1% vs last month
              </p>
            </div>
            <Users className="h-8 w-8 text-muted-foreground" />
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="workflow" className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
          <TabsTrigger value="workflow">Kitchen Workflow</TabsTrigger>
          <TabsTrigger value="labor">Labor Analysis</TabsTrigger>
          <TabsTrigger value="station">Station Optimization</TabsTrigger>
          <TabsTrigger value="benchmarking">Benchmarking</TabsTrigger>
        </TabsList>
        
        <TabsContent value="workflow" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Recipe Complexity vs. Preparation Time</CardTitle>
                <CardDescription>
                  Analysis of recipe complexity and preparation times
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={recipeComplexityData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                      barSize={20}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="complexity" name="Complexity Score" fill="#8884d8" />
                      <Bar yAxisId="right" dataKey="prepTime" name="Prep Time (min)" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Kitchen Bottleneck Analysis</CardTitle>
                <CardDescription>
                  Identifying workflow bottlenecks by station
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={bottleneckData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                      barSize={20}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="station" 
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="utilization" name="Utilization %" fill="#3b82f6" />
                      <Bar dataKey="avgWaitTime" name="Avg Wait Time (min)" fill="#f97316" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Workflow Bottlenecks & Recommendations</CardTitle>
              <CardDescription>
                Detailed analysis of identified bottlenecks with recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Station</TableHead>
                    <TableHead>Utilization</TableHead>
                    <TableHead>Wait Time</TableHead>
                    <TableHead>Bottleneck Reason</TableHead>
                    <TableHead>Recommendation</TableHead>
                    <TableHead className="text-right">Severity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bottleneckData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.station}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{item.utilization}%</span>
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                item.utilization > 85 
                                  ? "bg-red-500" 
                                  : item.utilization > 70 
                                    ? "bg-amber-500" 
                                    : "bg-green-500"
                              }`}
                              style={{ width: `${item.utilization}%` }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{item.avgWaitTime} min</TableCell>
                      <TableCell className="max-w-[200px]">{item.bottleneckReason}</TableCell>
                      <TableCell className="max-w-[200px]">{item.recommendation}</TableCell>
                      <TableCell className="text-right">
                        <Badge className={
                          item.utilization > 85 
                            ? 'bg-red-100 text-red-800 hover:bg-red-100' 
                            : item.utilization > 70
                              ? 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                              : 'bg-green-100 text-green-800 hover:bg-green-100'
                        }>
                          {item.utilization > 85 ? 'Critical' : item.utilization > 70 ? 'Moderate' : 'Low'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="labor" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Labor Cost Analysis By Menu Item</CardTitle>
              <CardDescription>
                Breakdown of labor costs associated with each menu item
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={laborCostData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                      barSize={20}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="item" 
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="prepTime" name="Prep Time (min)" stackId="a" fill="#3b82f6" />
                      <Bar dataKey="cookTime" name="Cook Time (min)" stackId="a" fill="#93c5fd" />
                      <Bar dataKey="platingTime" name="Plating Time (min)" stackId="a" fill="#bfdbfe" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={laborCostData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="item" 
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="laborCost" name="Labor Cost ($)" fill="#f97316" />
                      <Bar yAxisId="right" dataKey="percentOfPrice" name="% of Menu Price" fill="#fdba74" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Menu Item</TableHead>
                    <TableHead>Prep Time</TableHead>
                    <TableHead>Cook Time</TableHead>
                    <TableHead>Total Time</TableHead>
                    <TableHead>Labor Cost</TableHead>
                    <TableHead className="text-right">% of Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {laborCostData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.item}</TableCell>
                      <TableCell>{item.prepTime} min</TableCell>
                      <TableCell>{item.cookTime} min</TableCell>
                      <TableCell>{item.totalTime} min</TableCell>
                      <TableCell>${formatNumber(item.laborCost)}</TableCell>
                      <TableCell className="text-right">
                        <Badge className={
                          item.percentOfPrice > 30 
                            ? 'bg-red-100 text-red-800 hover:bg-red-100' 
                            : item.percentOfPrice > 25
                              ? 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                              : 'bg-green-100 text-green-800 hover:bg-green-100'
                        }>
                          {item.percentOfPrice}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="station" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Kitchen Station Organization</CardTitle>
              <CardDescription>
                Station usage patterns and reorganization recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stationData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="menuItems"
                        nameKey="station"
                        label={({ station }) => station}
                      >
                        {stationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [`${value} Items`, props.payload.station]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={stationData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="station" 
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="ingredientCount" name="Ingredient Count" fill="#8884d8" />
                      <Bar dataKey="utilizationRate" name="Utilization %" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Station</TableHead>
                    <TableHead>Ingredients</TableHead>
                    <TableHead>Menu Items</TableHead>
                    <TableHead>Utilization</TableHead>
                    <TableHead>Recommendation</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stationData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.station}</TableCell>
                      <TableCell>{item.ingredientCount}</TableCell>
                      <TableCell>{item.menuItems}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{item.utilizationRate}%</span>
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                item.utilizationRate > 85 
                                  ? "bg-red-500" 
                                  : item.utilizationRate > 70 
                                    ? "bg-amber-500" 
                                    : "bg-green-500"
                              }`}
                              style={{ width: `${item.utilizationRate}%` }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px]">{item.recommendation}</TableCell>
                      <TableCell className="text-right">
                        {item.utilizationRate > 85 ? (
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 flex items-center gap-1 justify-center">
                            <AlertCircle className="h-3 w-3" />
                            Overloaded
                          </Badge>
                        ) : item.utilizationRate < 60 ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 flex items-center gap-1 justify-center">
                            <AlertCircle className="h-3 w-3" />
                            Underutilized
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex items-center gap-1 justify-center">
                            <CheckCircle className="h-3 w-3" />
                            Optimal
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="benchmarking" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Industry Benchmarking</CardTitle>
              <CardDescription>
                Comparison of your operational metrics with industry standards
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={benchmarkingData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="yourRestaurant" name="Your Restaurant" stroke="#3b82f6" strokeWidth={2} />
                      <Line type="monotone" dataKey="industryAverage" name="Industry Average" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" />
                      <Line type="monotone" dataKey="topPerformers" name="Top Performers" stroke="#10b981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Performance Metrics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Operational Efficiency</span>
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            Above Average
                          </Badge>
                        </div>
                        <div className="w-full flex items-center gap-2">
                          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-green-500" style={{ width: '74%' }} />
                          </div>
                          <span className="text-xs font-medium">74%</span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Industry Avg: 68%</span>
                          <span>Top Performers: 82%</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Labor Cost Ratio</span>
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                            Average
                          </Badge>
                        </div>
                        <div className="w-full flex items-center gap-2">
                          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500" style={{ width: '29%' }} />
                          </div>
                          <span className="text-xs font-medium">29%</span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Industry Avg: 30%</span>
                          <span>Top Performers: 25%</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Preparation Time</span>
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            Above Average
                          </Badge>
                        </div>
                        <div className="w-full flex items-center gap-2">
                          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-green-500" style={{ width: '65%' }} />
                          </div>
                          <span className="text-xs font-medium">24.5 min</span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Industry Avg: 28 min</span>
                          <span>Top Performers: 22 min</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Improvement Opportunities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-1 bg-red-100 p-2 rounded-full">
                          <XCircle className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Dish Pit Efficiency</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            95% utilization vs. 80% industry benchmark
                          </p>
                          <Button variant="link" className="px-0 h-8 text-red-600">
                            View Action Plan
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-1 bg-amber-100 p-2 rounded-full">
                          <AlertCircle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Grill Station Redesign</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Redesign could improve prep time by 15%
                          </p>
                          <Button variant="link" className="px-0 h-8 text-amber-600">
                            View Action Plan
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-1 bg-green-100 p-2 rounded-full">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">Prep Time Reduction</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Your prep time is 12.5% better than average
                          </p>
                          <Button variant="link" className="px-0 h-8 text-green-600">
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OperationalEfficiencyAnalyzer;
